import React, { useEffect, useRef, useState } from "react";
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import XHRUpload from "@uppy/xhr-upload";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { baseURL } from "../../services";

const Upload = ({carId,inspectionId,onClose,onUploaded}) => {

    // Same lookup order as axiosHandler.js: the session token only lives in
    // localStorage when the user ticked "Remember me" - otherwise it is in
    // sessionStorage, and reading localStorage alone sent `Bearer null`.
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const dashboardRef = useRef(null);
    // The Uppy instance is owned by the effect below - one per car/inspection - instead
    // of by a ref shared across every render. It is stored together with the key it was
    // built for, so a render that has already moved on to a different inspection shows
    // nothing rather than the instance the effect cleanup is about to destroy.
    const instanceKey = `${carId}|${inspectionId}`;
    const [uppyState, setUppyState] = useState(null);
    // Set once an upload has finished, to close the picker without destroying the
    // instance the render still points at.
    const [closed, setClosed] = useState(false);
    // Held in a ref so an inline onClose from the parent does not re-run the effect
    // (and rebuild Uppy) on every parent render.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const onUploadedRef = useRef(onUploaded);
    onUploadedRef.current = onUploaded;

    useEffect(() => {
        const instance = new Uppy({
            // Must mirror the server limits on POST /cars/inspection/upload
            // (MAX_INSPECTION_FILE_BYTES = 100 * 1024 * 1024 and MAX_INSPECTION_FILES = 60
            // in Backend/app/routes/cars.js). Uppy bundles every file into a single
            // request, so exceeding either limit fails the WHOLE inspection set with a
            // 400 — catching it in the picker gives a far better message.
            // maxFileSize is deliberately 100000000 (100 MB decimal), a little BELOW the
            // server's 104857600 (100 MiB), so the picker always rejects first and no
            // file that passes here can be refused by multer. Do not raise it above the
            // server constant.
            restrictions: {
                maxFileSize: 100000000,
                maxNumberOfFiles: 60,
                allowedFileTypes: ["image/*", "video/*"],
            },
            meta: { carId, inspectionId },
        }).use(XHRUpload, {
            endpoint: `${baseURL}/cars/inspection/upload`,
            fieldName: "inspectionImages",
            bundle: true,
            formData: true,
            headers: {
                Authorization: `Bearer ${token}`,
                "Access-Control-Allow-Origin": "/*"
            },
            getResponseData: (responseText) => responseText,
        });

        let closeTimer = null;

        const closeUploader = () => {
            setClosed(true);
            // Letting the parent unmount us is what makes a second upload possible:
            // the next mount builds a brand new instance instead of reusing a
            // destroyed one.
            if (onCloseRef.current) {
                onCloseRef.current();
            }
        };

        // Close uploader after successful upload
        instance.on("complete", (result) => {
            console.log("Upload successful! Closing Uppy...");
            // Lets the parent reload the inspection so the new media shows at once.
            if (result?.successful?.length && onUploadedRef.current) {
                onUploadedRef.current();
            }
            closeTimer = setTimeout(closeUploader, 2000);
        });

        // Close when clicking outside
        const handleClickOutside = (event) => {
            if (dashboardRef.current && !dashboardRef.current.contains(event.target)) {
                closeUploader();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        // A new car/inspection means a fresh picker, even if the previous one had
        // already been closed after an upload.
        setUppyState({ key: instanceKey, instance });
        setClosed(false);

        return () => {
            if (closeTimer) {
                clearTimeout(closeTimer);
            }
            document.removeEventListener("mousedown", handleClickOutside);
            instance.destroy();
        };
    }, [carId, inspectionId, instanceKey, token]);

    if (closed || uppyState?.key !== instanceKey) {
        return null;
    }

    return (
        <div>
            <h2>Upload Images & Videos</h2>
            <Dashboard uppy={uppyState.instance} key={instanceKey} />
        </div>
    );
};

export default Upload;
